/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { GridApi } from 'ag-grid-community';
import { GridOptionsExt } from '../ext/grid-options-ext';
import { GRID_FEATURE, GridFeature, GridFeatureInitParams, GridFeatureReadyParams } from '../feature.interfaces';
import { GridViewportRowModel } from './grid-viewport-row-model';
import { GridViewportRange, GridViewportState } from './grid-viewport.interfaces';
import { ImmutableGridViewport } from './immutable-grid-viewport';

export const EMPTY_GRID_VIEWPORT_STATE = { rowCount: -1, rowData: [], loading: false, range: { start: 0, end: -1 } };

@Directive({
  selector: '[ghGridViewportState]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridViewportStateDirective,
    multi: true,
  }],
})
export class GridViewportStateDirective implements GridFeature {
  @Output() viewportChanged = new EventEmitter<GridViewportRange>();

  private _state: GridViewportState<any> = EMPTY_GRID_VIEWPORT_STATE;
  private initialized = false;
  private api: GridApi;

  constructor() {
    this.onViewportChanged = this.onViewportChanged.bind(this);
  }

  @Input('ghGridViewportState')
  set state(state: GridViewportState<any>) {
    if (this.initialized) {
      this.applyState(state);
    } else {
      this._state = state;
    }
  }

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  enhanceGridOptions(options: GridOptionsExt): void {
    options.rowModelType = 'viewport';
    options.viewport = new ImmutableGridViewport();
  }

  onGridReady(params: GridFeatureReadyParams): void {
    const { api } = params;
    this.initialized = true;
    this.api = api;

    const state = this._state;
    this._state = EMPTY_GRID_VIEWPORT_STATE;
    this.state = state;

    api.addEventListener(GridViewportRowModel.EVENT_VIEWPORT_RANGE_CHANGED, this.onViewportChanged);

    const model = <GridViewportRowModel>api.getModel();
    this.viewportChanged.emit(model.range);
  }

  onDestroy(): void {
    this.api.removeEventListener(GridViewportRowModel.EVENT_VIEWPORT_RANGE_CHANGED, this.onViewportChanged);
    // nothing to do
  }

  private onViewportChanged({ range }: { range: GridViewportRange }): void {
    this.viewportChanged.emit(range);
  }

  private applyState(state: GridViewportState<any>): void {
    const oldState = this._state;
    const { api } = this;
    const model = <GridViewportRowModel>api.getModel();

    if (oldState.loading !== state.loading) {
      if (state.loading) {
        api.showLoadingOverlay();
      } else {
        api.hideOverlay();
      }
    }
    if (oldState.rowCount !== state.rowCount) {
      model.setRowCount(state.rowCount);
    }
    if (oldState.rowData !== state.rowData) {
      model.setRowData(state.rowData);
    }

    this._state = state;
  }
}
